import { type MouseEvent as ReactMouseEvent, useRef, useState } from "react";
import { ChevronLeftIcon, XIcon } from "lucide-react";

import { SettlementTreatmentEnum } from "@/__generated__/graphql";
import { HotkeyDecorator } from "@/components/atoms/HotkeyDecorator";
import { Icon } from "@/components/atoms/Icon";
import { Popper } from "@/components/atoms/Popper";
import { List } from "@/components/organisms/List";
import { iconsByTypename } from "@/objects/@icons";
import { State } from "@/state";
import { Spinner } from "@/components/ui/spinner";
import { Button } from "@/components/ui/button";
import { Typography } from "@/components/ui/typography";

import { TriageChip, TriageMovedChip } from "./TriageReconcileRow";
import type {
  TriageActiveRowStaging,
  TriageDetailContextValue,
} from "./TriageView.context";
import { TRIAGE_SETTLEMENTS } from "./TriageView.content";
import { TRIAGE_KEYS } from "./TriageView.keys";

/** A business to move the payment to. */
type TriageMoveBusinessRow = { identifier: string; name: string };

/** One way two businesses settle a payment, to move it there by. */
type TriageMoveSettlementRow = {
  treatment: SettlementTreatmentEnum;
  title: string;
  /** In the two businesses' names - none till the payer's is read. */
  description: string | null;
  isRecommended: boolean;
};

/**
 * Right about what the money was for, not whose it was: the open payment's
 * Move, a chip on its own row - dashed, something it can be done to it;
 * solid once it is moved - the payment moved to another of the person's businesses as
 * a whole, every suggestion worked out again settled there, and booked so; or
 * back to its own. It, or M, opens on the businesses; another picked, how the
 * two settle it. Moved - or a suggestion picked settling it in another - its x
 * takes that back. Where it is for is its row's half's to say.
 */
export function TriageMovePayment({
  triage,
  selections,
  movePayment,
  movingPaymentTo,
  isProcessing,
}: {
  triage: TriageActiveRowStaging["triage"];
  selections: TriageActiveRowStaging["selections"];
  movePayment: TriageDetailContextValue["movePayment"];
  movingPaymentTo: string | null;
  /** The AI at work on it, or a move of it asked: nothing more asked of it meanwhile. */
  isProcessing: boolean;
}) {
  const entities = State((state) => state.entities);
  const [isOpen, setIsOpen] = useState(false);
  // The business picked, first: then how the two settle it.
  const [chosen, setChosen] = useState<TriageMoveBusinessRow | null>(null);
  // Where the keyboard was as it opened, given back as it shuts.
  const focusedRef = useRef<HTMLElement | null>(null);
  const payer = triage.entity;
  // Moved to another business - or, not moved, its suggestion picked settling
  // in one by its rule.
  const picked = (triage.suggestions ?? []).find(
    (suggestion) =>
      suggestion.identifier === selections.linesSuggestionIdentifier,
  );
  const settlesIn =
    picked?.lines?.find(
      (line) => line.entity && line.entity.identifier !== payer?.identifier,
    )?.entity ?? null;
  const isMoved = triage.belongsToEntity != null;
  const movedTo = isMoved ? triage.belongsToEntity : settlesIn;
  const isOut = Number(triage.amount) < 0;
  // Moved already, its own business first - back to it - then the rest.
  const businesses: TriageMoveBusinessRow[] = [
    ...(isMoved && payer?.name
      ? [{ identifier: payer.identifier, name: payer.name }]
      : []),
    ...entities
      .filter(
        (entity) =>
          entity.identifier !== payer?.identifier &&
          entity.identifier !== movedTo?.identifier,
      )
      .map((entity) => ({ identifier: entity.identifier, name: entity.name })),
  ];
  // Nowhere to move it: none.
  if (businesses.length === 0 && !movedTo) return null;
  // How the two businesses settle it, in their names - the money from the
  // payer when it went out, to it when it came in.
  const settlementsWith = (payee: string): TriageMoveSettlementRow[] =>
    TRIAGE_SETTLEMENTS.filter((row) => !isOut || !row.isTakenInOnly).map(
      (row) => ({
        treatment: row.treatment,
        title: row.title,
        isRecommended: row.isRecommended,
        description: payer?.name
          ? isOut
            ? row.describe(payer.name, payee)
            : row.describe(payee, payer.name)
          : null,
      }),
    );
  // Booked into another business, it is there: taken back by unbooking it.
  const isSettledThere = Boolean(triage.isBooked) && isMoved;
  // Its x says it is its own business's after all - what was said taken back:
  // moved, or a suggestion settling it in another; not while it is being
  // moved, nor once booked there.
  const isTakenBackByX = movedTo != null && payer != null && !isSettledThere;
  const isBusy = isProcessing || movingPaymentTo != null;
  // Its key, M, after its words.
  const hotkey = (
    <HotkeyDecorator
      variant="outlined"
      hotkey={TRIAGE_KEYS.MOVE}
      color="inherit"
    />
  );
  const toggle = (event: ReactMouseEvent) => {
    // A control of the row's own: picking it is not picking the row.
    event.stopPropagation();
    if (!isOpen) {
      focusedRef.current = document.activeElement as HTMLElement | null;
      // Moved already: opened on how it settles, the businesses behind.
      if (movedTo?.name) {
        setChosen({ identifier: movedTo.identifier, name: movedTo.name });
      }
    }
    setIsOpen(!isOpen);
  };

  return (
    <div className="flex items-center gap-0.5">
      <Popper
        isOpenState={[
          isOpen,
          (open: boolean) => {
            setIsOpen(open);
            if (!open) setChosen(null);
          },
        ]}
        isClickable={false}
        placement="bottom-start"
        floatingStyle={{ width: chosen ? 340 : 260 }}
        focusRestoreRef={focusedRef}
        anchor={
          isMoved && movedTo?.name ? (
            // Moved: the business it is for, on the chip every other
            // business is drawn on - on the books, solid - its key, M, after.
            <TriageMovedChip
              testId="triage-payment-move"
              payment={triage}
              disabled={isBusy || isSettledThere}
              endDecorator={hotkey}
              onClick={toggle}
            />
          ) : (
            // Not yet: something that can be done to it, dashed.
            <TriageChip
              testId="triage-payment-move"
              state="suggested"
              disabled={isBusy || isSettledThere}
              startDecorator={
                movingPaymentTo != null ? (
                  <Spinner />
                ) : (
                  <Icon ReactIcon={iconsByTypename.EntityObject} size="xs" />
                )
              }
              endDecorator={hotkey}
              onClick={toggle}
            >
              Move
            </TriageChip>
          )
        }
      >
        <div data-testid="triage-payment-move-panel">
          {chosen ? (
            <TriageMoveSettles
              chosen={chosen}
              settlements={settlementsWith(chosen.name)}
              onBack={() => setChosen(null)}
              onMove={(treatment) => {
                setIsOpen(false);
                setChosen(null);
                movePayment(chosen.identifier, treatment);
              }}
            />
          ) : (
            <List<TriageMoveBusinessRow>
              key="businesses"
              data={businesses}
              getRowKey={(row) => row.identifier}
              onItemClick={(row) => {
                // Its own business again: nothing between two to settle -
                // moved back at once.
                if (row.identifier === payer?.identifier) {
                  setIsOpen(false);
                  movePayment(row.identifier, null);
                  return;
                }
                setChosen(row);
              }}
              autofocus
              fillHeight={false}
              size="sm"
            >
              <List.Items<TriageMoveBusinessRow>>
                {(row: TriageMoveBusinessRow) => (
                  <List.Item
                    value={row}
                    data-testid={`triage-payment-move-${row.identifier}`}
                  >
                    <Typography level="body-md" className="truncate pl-1 pr-1">
                      {row.identifier === payer?.identifier
                        ? `Back to ${row.name}`
                        : row.name}
                    </Typography>
                  </List.Item>
                )}
              </List.Items>
            </List>
          )}
        </div>
      </Popper>
      {isTakenBackByX && payer ? (
        // Moved, or a suggestion settling it into another business: its x
        // takes that back.
        <Button
          variant="ghost"
          size="icon-xs"
          aria-label={payer.name ? `Keep in ${payer.name}` : undefined}
          data-testid="triage-payment-move-clear"
          disabled={isBusy}
          onMouseDown={(event) => event.preventDefault()}
          onClick={(event) => {
            event.stopPropagation();
            movePayment(payer.identifier, null);
          }}
          className="text-muted-foreground/80"
        >
          <XIcon size={16} />
        </Button>
      ) : null}
    </div>
  );
}

/**
 * The business picked, in its Move: how the two settle it - picked, the
 * payment moved there so (``move_transactions``). Its payee's others go with
 * it from Similar's bulk list, not from here.
 */
function TriageMoveSettles({
  chosen,
  settlements,
  onBack,
  onMove,
}: {
  chosen: TriageMoveBusinessRow;
  settlements: TriageMoveSettlementRow[];
  onBack: () => void;
  onMove: (treatment: SettlementTreatmentEnum) => void;
}) {
  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        data-testid="triage-payment-move-back"
        onMouseDown={(event) => event.preventDefault()}
        onClick={onBack}
        className="m-1 font-medium"
      >
        <Icon ReactIcon={ChevronLeftIcon} size="sm" />
        {chosen.name}
      </Button>
      <List<TriageMoveSettlementRow>
        key="settlements"
        data={settlements}
        getRowKey={(row) => row.treatment}
        onItemClick={(row) => onMove(row.treatment)}
        autofocus
        fillHeight={false}
        size="sm"
      >
        <List.Items<TriageMoveSettlementRow>>
          {(row: TriageMoveSettlementRow) => (
            <List.Item
              value={row}
              data-testid={`triage-payment-move-settles-${row.treatment}`}
            >
              <div className="pl-1 pr-1 pt-0.5 pb-0.5 min-w-0">
                <Typography level="body-md">
                  {row.title}
                  {row.isRecommended ? (
                    <Typography
                      component="span"
                      level="body-xs"
                      className="text-muted-foreground/80 ml-1.5"
                    >
                      Recommended
                    </Typography>
                  ) : null}
                </Typography>
                {row.description ? (
                  <Typography
                    level="body-xs"
                    className="text-muted-foreground/80 whitespace-normal"
                  >
                    {row.description}
                  </Typography>
                ) : null}
              </div>
            </List.Item>
          )}
        </List.Items>
      </List>
    </>
  );
}
